import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getWashPackages } from "./washPackages";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

function Home({ user }) {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  // Phone verification alert state
  const [showPhoneVerificationAlert, setShowPhoneVerificationAlert] = useState(false);
  
  // Session management refs
  const isCheckingSession = useRef(false);

  const carouselImages = [
    "/images/carousel/red-car-wash.png",
    "/images/carousel/Caravan_car_wash.jpg",
    "/images/carousel/tundra_car_wash.jpg",
    "/images/carousel/suv-car-wash.png",
    "/images/carousel/porchegt_car_wash.jpg"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const membershipPlans = getWashPackages();
  
  // Flag to prevent multiple redirects
  const hasRedirected = useRef(false);
  
  // Helper function to handle redirect cleanup
  const handleRedirectCleanup = () => {
    if (hasRedirected.current) {
      console.log('Already redirected, skipping additional redirect');
      return true; // Already redirected
    }
    
    hasRedirected.current = true;
    return false; // Not redirected yet
  };
  
  // Check for existing booking sessions
  const checkForExistingSessions = () => {
    if (!auth.currentUser) {
      console.log('No authenticated user, no sessions to check');
      return { hasSession: false, sessionType: null };
    }
    
    console.log('Checking sessions for user:', auth.currentUser.uid);
    
    // Check for global booking session first (persistent across browser restarts)
    const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
    const globalSession = localStorage.getItem(globalSessionKey);
    
    if (globalSession) {
      try {
        const globalSessionData = JSON.parse(globalSession);
        const now = Date.now();
        const age = now - globalSessionData.timestamp;
        
        console.log('Found global session:', globalSessionData.page, 'age:', Math.round(age/1000), 'seconds');
        
        // Ultra aggressive cleanup: 10 seconds for age, 2 seconds for heartbeat
        if (age > 10 * 1000 || (globalSessionData.lastHeartbeat && Date.now() - globalSessionData.lastHeartbeat > 2 * 1000)) {
          console.log('Global session is stale, removing');
          localStorage.removeItem(globalSessionKey);
        } else {
          // Additional validation: ensure session has required fields
          if (globalSessionData.page && globalSessionData.timestamp && (globalSessionData.page === 'appointment' || globalSessionData.page === 'payment')) {
            console.log('Active global session found - BLOCKING');
            return { hasSession: true, sessionType: globalSessionData.page }; // Return session type
          } else {
            console.log('Invalid global session data, removing');
            localStorage.removeItem(globalSessionKey);
          }
        }
      } catch (error) {
        console.log('Invalid global session data, removing');
        localStorage.removeItem(globalSessionKey);
      }
    }
    
    // Check for appointment page session (sessionStorage - current session only)
    const appointmentSessionKey = `appointment_session_${auth.currentUser.uid}`;
    const appointmentSession = sessionStorage.getItem(appointmentSessionKey);
    
    if (appointmentSession) {
      try {
        const sessionData = JSON.parse(appointmentSession);
        const now = Date.now();
        const age = now - sessionData.timestamp;
        
        console.log('Found appointment session:', sessionData.page, 'age:', Math.round(age/1000), 'seconds');
        
        // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 or 1 (just created)
        if (age > 2 * 1000 || age === 0 || age === 1000) {
          console.log('Appointment session is stale or just created (0-1 seconds), removing');
          sessionStorage.removeItem(appointmentSessionKey);
        } else {
          // Additional validation: ensure session has required fields
          if (sessionData.page && sessionData.timestamp && sessionData.page === 'appointment') {
            console.log('Active appointment session found - BLOCKING');
            return { hasSession: true, sessionType: 'appointment' }; // Return session type
          } else {
            console.log('Invalid appointment session data, removing');
            sessionStorage.removeItem(appointmentSessionKey);
          }
        }
      } catch (error) {
        console.log('Invalid appointment session data, removing');
        sessionStorage.removeItem(appointmentSessionKey);
      }
    }
    
    // Check for payment page session (sessionStorage - current session only)
    const paymentSessionKey = `payment_session_${auth.currentUser.uid}`;
    const paymentSession = sessionStorage.getItem(paymentSessionKey);
    
    if (paymentSession) {
      try {
        const sessionData = JSON.parse(paymentSession);
        const now = Date.now();
        const age = now - sessionData.timestamp;
        
        console.log('Found payment session:', sessionData.page, 'age:', Math.round(age/1000), 'seconds');
        
        // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 or 1 (just created)
        if (age > 2 * 1000 || age === 0 || age === 1000) {
          console.log('Payment session is stale or just created (0-1 seconds), removing');
          sessionStorage.removeItem(paymentSessionKey);
        } else {
          // Additional validation: ensure session has required fields
          if (sessionData.page && sessionData.timestamp && sessionData.page === 'payment') {
            console.log('Active payment session found - BLOCKING');
            return { hasSession: true, sessionType: 'payment' }; // Return session type
          } else {
            console.log('Invalid payment session data, removing');
            sessionStorage.removeItem(paymentSessionKey);
          }
        }
      } catch (error) {
        console.log('Invalid payment session data, removing');
        sessionStorage.removeItem(paymentSessionKey);
      }
    }
    
    console.log('No active sessions found');
    return { hasSession: false, sessionType: null }; // No active sessions
  };
  
  // Handle appointment selection with session checking
  const handleAppointmentSelection = () => {
    console.log('Appointment selected - checking for existing sessions...');
    
    // Check for existing sessions before navigating
    const sessionCheck = checkForExistingSessions();
    console.log('Session check result:', sessionCheck);
    
    if (sessionCheck.hasSession) {
      console.log('CONFLICT DETECTED - blocking navigation for:', sessionCheck.sessionType);
      
      // Force cleanup any existing sessions before showing error
      if (window.forceCleanupAppointmentSession) {
        window.forceCleanupAppointmentSession();
      }
      
      // Add a small delay to ensure cleanup completes
      setTimeout(() => {
        // Use the specific session type for more accurate error message
        if (sessionCheck.sessionType === 'payment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('A payment page is already open. Redirecting to home page.');
          navigate('/');
        } else if (sessionCheck.sessionType === 'appointment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('An appointment page is already open. Redirecting to home page.');
          navigate('/');
        } else {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('You already have an active booking session. Redirecting to home page.');
          navigate('/');
        }
      }, 100); // 100ms delay to ensure cleanup
      
      return;
    }
    
    console.log('No conflicts - proceeding to appointment page');
    
    // Navigate to appointment page with service data
    navigate('/appointment', { 
      state: { 
        serviceData: {
          name: selectedService.name,
          price: selectedService.price.replace('$', ''),
          description: selectedService.description
        }
      }
    });
    
    // Close modal
    setShowModal(false);
    setSelectedService(null);
  };

  // Handle drive-in selection with session checking
  const handleDriveInSelection = () => {
    console.log('Drive-in selected - checking for existing sessions...');
    
    // Check for existing sessions before navigating
    const sessionCheck = checkForExistingSessions();
    console.log('Session check result:', sessionCheck);
    
    if (sessionCheck.hasSession) {
      console.log('CONFLICT DETECTED - blocking navigation for:', sessionCheck.sessionType);
      
      // Force cleanup any existing sessions before showing error
      if (window.forceCleanupAppointmentSession) {
        window.forceCleanupAppointmentSession();
      }
      
      // Add a small delay to ensure cleanup completes
      setTimeout(() => {
        // Use the specific session type for more accurate error message
        if (sessionCheck.sessionType === 'payment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('A payment page is already open. Redirecting to home page.');
          navigate('/');
        } else if (sessionCheck.sessionType === 'appointment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('An appointment page is already open. Redirecting to home page.');
          navigate('/');
        } else {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('You already have an active booking session. Redirecting to home page.');
          navigate('/');
        }
      }, 100); // 100ms delay to ensure cleanup
      
      return;
    }
    
    console.log('No conflicts - proceeding to payment page');
    
    // Navigate to payment page with service data in the format expected by Payment component
    navigate('/payment', { 
      state: { 
        name: selectedService.name,
        price: selectedService.price.replace('$', ''),
        description: selectedService.description,
        isDriveIn: true
      }
    });
    
    // Close modal
    setShowModal(false);
    setSelectedService(null);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedService(null);
  };

  // Check if user's phone number is verified
  const checkPhoneVerification = async () => {
    if (!user) {
      return false;
    }
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.phoneVerified === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking phone verification:', error);
      return false;
    }
  };

  // Handle service selection - show modal immediately without session checking
  const handleServiceSelection = async (service) => {
    if (!user) {
      // User is not logged in, redirect to auth page with redirect to services
      navigate('/auth', { state: { redirectPath: '/services' } });
      return;
    }
    
    // Check if phone is verified
    const isPhoneVerified = await checkPhoneVerification();
    if (!isPhoneVerified) {
      setShowPhoneVerificationAlert(true);
      return;
    }
    
    console.log('=== SERVICE SELECTION ===');
    console.log('Showing service options modal');
    
    // Show the modal with service options immediately
    setSelectedService(service);
    setShowModal(true);
  };

  return (
    <div>
      {/* Hero Section with Left Content and Right Carousel */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        minHeight: '70vh',
        padding: '2rem',
        background: '#3c3c3c'
      }}>
        {/* Left Side - Welcome Content */}
        <div style={{ 
          flex: 1, 
          textAlign: 'left', 
          paddingRight: '3rem',
          maxWidth: '500px'
        }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            color: '#f97316',
            marginBottom: '1rem',
            lineHeight: '1.2'
          }}>
            Rocket Express Auto Wash
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            color: '#fff', 
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            100% Touch-Free Technology. Reliable, Fast, and friendly every time!
          </p>
          <button 
            onClick={async () => {
              if (user) {
                // Check if phone is verified before navigating to services
                const isPhoneVerified = await checkPhoneVerification();
                if (!isPhoneVerified) {
                  setShowPhoneVerificationAlert(true);
                  return;
                }
                // User is logged in and phone verified, navigate to services
                navigate('/services');
              } else {
                // User is not logged in, navigate to auth page with redirect to services
                navigate('/auth', { state: { redirectPath: '/services' } });
              }
            }}
            style={{ 
            padding: '1rem 2.5rem', 
            background: '#f97316', 
            color: '#fff', 
            border: 'none', 
            borderRadius: '8px', 
            fontSize: '1.2rem', 
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Get Wash
          </button>
        </div>

        {/* Right Side - Image Carousel */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          height: '400px',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
        }}>
          <img 
            src={carouselImages[currentImageIndex]} 
            alt={`Car wash ${currentImageIndex + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'opacity 0.5s ease-in-out'
            }}
          />
          
          {/* Carousel Navigation Dots */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '10px'
          }}>
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  border: 'none',
                  background: index === currentImageIndex ? '#f97316' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'background 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Benefits with Free Membership Heading */}
      <div style={{
        width: '100%',
        background: '#fff',
        padding: '1.5rem 0',
        textAlign: 'center',
      }}>
        <h2 style={{
          color: '#3c3c3c',
          fontSize: '2rem',
          fontWeight: 'bold',
          margin: 0,
          letterSpacing: '1px',
        }}>
          Gain access to <span style={{fontWeight: '900'}}>BENEFITS</span> with our free membership. Use our Android/iOS app for faster processing
        </h2>
        <div style={{
          color: '#1f2937',
          fontSize: '1.43rem',
          marginTop: '0.5rem',
          fontWeight: 500
        }}>
          Book an appointment online, at location, or just drive in
        </div>
        
        <div style={{
          color: 'red',
          fontSize: '1.43rem',
          marginTop: '1rem',
          fontWeight: 500,
          textAlign: 'center'
        }}>
          Free Online Appointments
        </div>
        <div style={{
          color: 'red',
          fontSize: '1.43rem',
          marginTop: '0.5rem',
          fontWeight: 500,
          textAlign: 'center'
        }}>
          Plus $5 to $10 for large vehicles
        </div>
        <div style={{
          color: 'red',
          fontSize: '1.43rem',
          marginTop: '0.5rem',
          fontWeight: 500,
          textAlign: 'center'
        }}>
          All fees are paid at location
        </div>
        <div style={{
          color: 'red',
          fontSize: '1.43rem',
          marginTop: '0.5rem',
          fontWeight: 500,
          textAlign: 'center'
        }}>
          Monday to Saturday 7:00am to 7:00pm and Sunday and Public Holidays 11am to 7pm
        </div>
      </div>


      {/* Membership Plans Section */}
      <div style={{ 
        marginTop: '0',
        padding: '2rem',
        background: '#f97316'
      }}>
        <h2 style={{
          textAlign: 'center',
          fontSize: '2.5rem',
          fontWeight: 'bold',
          color: '#1f2937',
          marginBottom: '2rem'
        }}>
          WASH PACKAGE LINEUP
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '1.5rem', 
          marginTop: '2rem',
          padding: '0 1rem',
          maxWidth: '1200px',
          margin: '2rem auto'
        }}>
          {membershipPlans.map((plan, index) => (
            <div key={index} style={{
              background: plan.background,
              color: plan.textColor,
              borderRadius: '12px',
              padding: '2rem 1.5rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              position: 'relative',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h3 style={{ 
                fontSize: '1.8rem', 
                fontWeight: 'bold', 
                marginBottom: '0.5rem',
                color: plan.textColor
              }}>
                {plan.name}
              </h3>
              
              <p style={{ 
                fontSize: '1rem', 
                marginBottom: '2rem',
                color: plan.textColor
              }}>
                {plan.description}
              </p>

              <div style={{ flex: 1 }}>
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{
                      marginRight: '0.5rem',
                      color: feature.included ? plan.textColor : '#9ca3af',
                      fontSize: '1.1rem'
                    }}>
                      {feature.included ? '✓' : '✗'}
                    </span>
                    <span style={{
                      color: feature.included ? plan.textColor : '#9ca3af'
                    }}>
                      {feature.name}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Price - moved above BUY NOW button */}
              <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                {plan.isOnSale && (
                  <div style={{
                    fontSize: '1rem',
                    color: plan.textColor === 'white' ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                    textDecoration: 'line-through',
                    marginBottom: '0.15rem',
                  }}>
                    {plan.regularPrice}
                  </div>
                )}
                <span style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: 'bold',
                  color: plan.isOnSale ? '#dc2626' : plan.textColor
                }}>
                  {plan.price}
                </span>
                {plan.isOnSale && (
                  <div style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: '600', marginTop: '0.25rem' }}>
                    July Special
                  </div>
                )}
              </div>
              
              {/* BUY NOW Button */}
              <button 
                onClick={() => handleServiceSelection(plan)}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px) scale(1.02)';
                  e.target.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
                  e.target.style.background = '#ea580c';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  e.target.style.background = '#f97316';
                }}
              >
                BOOK NOW
              </button>
            </div>
          ))}
        </div>
        
        {/* Description Section */}
        <div style={{
          marginTop: '3rem',
          padding: '2rem',
          background: '#1f2937',
          borderRadius: '12px',
          maxWidth: '800px',
          margin: '3rem auto 0 auto'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#f97316',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            About Our Services
          </h3>
          <p style={{
            fontSize: '1rem',
            color: '#fff',
            lineHeight: '1.6',
            textAlign: 'center',
            margin: 0,
            marginBottom: '1.5rem'
          }}>
            Our advanced touchless system uses high-pressure water jets and precision-applied cleaning solutions to remove dirt without any physical contact. Sensors map your vehicle's contours for a complete, scratch-free clean.
          </p>
          <ul style={{
            color: '#fff',
            fontSize: '1rem',
            lineHeight: '1.8',
            maxWidth: '600px',
            margin: '0 auto',
            paddingLeft: '1.2em',
            listStyleType: 'disc',
          }}>
            <li>Free Appointments are available.</li>
            <li>Convenience and speed - no need to exit your vehicle. Washes are completed within 8 minutes to 12 minutes.</li>
            <li>Scratch free - No contact required.</li>
            <li>Safe for Ceramic Coating</li>
            <li>Coming soon - Free self-service vacuum use with every wash but not always available.</li>
            <li>Water Wax helps reduce the effects of rain and adds a slight shine for 2 to 3 days, depending on the environment.</li>
          </ul>
        </div>
      </div>

      {/* Service Options Modal */}
      {showModal && selectedService && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <h2 style={{
              color: '#1f2937',
              fontSize: '1.8rem',
              fontWeight: 'bold',
              marginBottom: '1rem'
            }}>
              Choose Your Service Type
            </h2>
            
            <div style={{
              background: '#f9fafb',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{
                color: '#f97316',
                fontSize: '1.3rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                {selectedService.name}
              </h3>
              <p style={{
                color: '#6b7280',
                marginBottom: '0.5rem'
              }}>
                {selectedService.description}
              </p>
              <div style={{
                color: selectedService.isOnSale ? '#dc2626' : '#f97316',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                {selectedService.isOnSale && (
                  <span style={{
                    display: 'block',
                    fontSize: '1rem',
                    color: '#9ca3af',
                    textDecoration: 'line-through',
                    fontWeight: '500',
                  }}>
                    {selectedService.regularPrice}
                  </span>
                )}
                {selectedService.price}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <button
                onClick={handleAppointmentSelection}
                style={{
                  padding: '1rem',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#ea580c';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f97316';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>📅</span>
                <span>Appointment</span>
                <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>Schedule a time</span>
              </button>

              <button
                onClick={handleDriveInSelection}
                disabled={true}
                style={{
                  padding: '1rem',
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'not-allowed',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#059669';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#10b981';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>🚗</span>
                <span>Drive In</span>
                <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>Wait in line</span>
              </button>
            </div>

            <button
              onClick={handleCloseModal}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Phone Verification Alert Modal */}
      {showPhoneVerificationAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}>
            <h3 style={{ 
              color: '#dc2626', 
              marginBottom: '1rem', 
              fontSize: '1.5rem', 
              fontWeight: 'bold' 
            }}>
              Phone Verification Required
            </h3>
            <p style={{ 
              color: '#4b5563', 
              marginBottom: '1.5rem', 
              fontSize: '1rem', 
              lineHeight: '1.5' 
            }}>
              You must verify your phone number before booking an appointment. Please go to your account page to verify your contact number.
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => setShowPhoneVerificationAlert(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPhoneVerificationAlert(false);
                  navigate('/account');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#ea580c';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f97316';
                }}
              >
                Go to Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home; 